import React, { useEffect, useRef, useCallback, useState } from "react";
import FlipperVaultCard from "../../../stories/common/flipper-vault-card";
import FlipperVaultCardSkeleton from "../../../skeleton/flipper-vault-card-skeleton";
import styles from "./index.module.scss";
import { useFlipperVault } from "../../../hooks/react-query/inv-exchange/useFlipperVault";

// Debounce utility function
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export default function GridView({
  mostVisibleCard,
  setMostVisibleCard,
  onVisibleCardChange
}: {
  mostVisibleCard: string | null;
  setMostVisibleCard: (acctId: string | null) => void;
  onVisibleCardChange?: (acctId: string | null) => void;
}) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useFlipperVault();
  const observerTarget = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(false);

  // Save scroll position on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (gridContainerRef.current?.parentElement) {
        const scrollPosition = gridContainerRef.current.parentElement.scrollLeft;
        sessionStorage.setItem(
          "flipper-vault-horizontal-scroll-position",
          scrollPosition.toString()
        );
      }
    };

    const parentElement = gridContainerRef.current?.parentElement;
    if (parentElement) {
      parentElement.style.scrollBehavior = "auto";
      parentElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (parentElement) {
        parentElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Restore scroll position only on initial load
  useEffect(() => {
    if (!isLoading && data?.pages && !shouldRestoreScroll) {
      const savedScrollPosition = sessionStorage.getItem(
        "flipper-vault-horizontal-scroll-position"
      );
      
      if (savedScrollPosition && gridContainerRef.current?.parentElement) {
        const parentElement = gridContainerRef.current.parentElement;
        parentElement.style.scrollBehavior = "auto";
        requestAnimationFrame(() => {
          parentElement.scrollLeft = parseInt(savedScrollPosition);
        });
      }
    }
  }, [isLoading, data?.pages, shouldRestoreScroll]);

  const handlePaginationObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        setShouldRestoreScroll(true);
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, mostVisibleCard]
  );

  const handleCardVisibility = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      let maxVisibility = 0;
      let mostVisibleId: string | null = null;

      entries.forEach((entry) => {
        if (entry.intersectionRatio > maxVisibility) {
          maxVisibility = entry.intersectionRatio;
          mostVisibleId = entry.target.getAttribute("data-acctid");
        }
      });

      if (mostVisibleId !== mostVisibleCard) {
        setMostVisibleCard(mostVisibleId);
        onVisibleCardChange?.(mostVisibleId);
      }
    },
    [mostVisibleCard, onVisibleCardChange]
  );

  // Create a debounced version of handleCardVisibility
  const debouncedHandleCardVisibility = useCallback(
    debounce((entries: IntersectionObserverEntry[]) => {
      handleCardVisibility(entries);
    }, 200), // 150ms delay
    [handleCardVisibility]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handlePaginationObserver, {
      threshold: 0.1
    });

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handlePaginationObserver]);

  useEffect(() => {
    const observer = new IntersectionObserver(debouncedHandleCardVisibility, {
      threshold: Array.from({ length: 20 }, (_, i) => i / 20)
    });

    const cards = document.querySelectorAll("[data-acctid]");
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [debouncedHandleCardVisibility, data?.pages]);

  if (isLoading) {
    return (
      <div className={styles.gridContainer}>
        {[1, 2, 3].map((key) => (
          <FlipperVaultCardSkeleton key={key} />
        ))}
      </div>
    );
  }

  return (
    <>
      <div ref={gridContainerRef} className={styles.gridContainer}>
        {data?.pages &&
          data?.pages?.map(
            (page: any) =>
              page?.items?.length > 0 &&
              page?.items?.map((vault: any, index: number) => (
                <div
                  key={`${vault.acctId}-${index}-${JSON.stringify(
                    vault.acctName
                  )}`}
                  data-acctid={vault.acctId}
                  className={styles.cardWrapper}
                >
                  <FlipperVaultCard
                    flipperData={vault}
                    mostVisibleId={mostVisibleCard}
                  />
                </div>
              ))
          )}
      </div>

      <div ref={observerTarget} className={styles.loadingTrigger}>
        {isFetchingNextPage && (
          <div className={styles.gridContainer}>
            {[1, 2, 3].map((key) => (
              <FlipperVaultCardSkeleton key={key} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
